import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { LoadingSpinner } from 'components/common';
import { getComparisonFile } from '../documentCompareApi';
import { errorOf } from '../compareFormat';
import { Notice } from '../ComparisonResult';

// The marked-up file shown in the page. A PDF opens in the browser's own viewer
// (highlights and notes are PDF annotations); a .docx is drawn by docx-preview,
// highlights included. `target` (a finding, re-sent with a new nonce) scrolls to it:
// the PDF to its page, the .docx to the hidden bookmark the annotator put on it.

const BOOKMARK_PREFIX = '_PEB_F'; // backend Document::Compare::DocxAnnotator::BOOKMARK_PREFIX
const FLASH = [{ backgroundColor: 'rgba(37, 99, 235, 0.25)' }, { backgroundColor: 'transparent' }];

const DocumentViewer = ({ comparison, target }) => {
  const [status, setStatus] = useState('loading');
  const [error, setError] = useState('');
  const [pdfUrl, setPdfUrl] = useState('');
  const docxRef = useRef(null);
  const isPdf = /\.pdf$/i.test(comparison.annotated_filename || '');

  useEffect(() => {
    let stale = false;
    let url = '';
    setStatus('loading');
    getComparisonFile(comparison.id)
      .then(async (blob) => {
        if (stale) return;
        if (isPdf) {
          url = URL.createObjectURL(new Blob([blob], { type: 'application/pdf' }));
          setPdfUrl(url);
        } else {
          const { renderAsync } = await import('docx-preview');
          if (stale || !docxRef.current) return;
          await renderAsync(blob, docxRef.current, undefined, {
            className: 'docx', inWrapper: true, ignoreLastRenderedPageBreak: true, renderComments: false,
          });
        }
        if (!stale) setStatus('ready');
      })
      .catch((e) => {
        if (stale) return;
        setStatus('error');
        setError(errorOf(e, 'Could not open the marked-up file. Download it instead.'));
      });
    return () => { stale = true; if (url) URL.revokeObjectURL(url); };
  }, [comparison.id, isPdf]);

  useEffect(() => {
    if (!target || isPdf || status !== 'ready' || !docxRef.current) return;
    const mark = docxRef.current.querySelector(`[id="${BOOKMARK_PREFIX}${target.finding.id}"]`);
    if (!mark) return;
    mark.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
    mark.closest('p')?.animate?.(FLASH, { duration: 1800 });
  }, [target, isPdf, status]);

  const page = isPdf && target?.finding.page;

  return (
    <div className="tw-relative tw-h-full tw-min-h-[480px] tw-rounded-xl tw-border tw-border-solid tw-border-slate-200 tw-bg-slate-100 tw-overflow-hidden">
      {status === 'loading' && (
        <div role="status" className="tw-absolute tw-inset-0 tw-flex tw-items-center tw-justify-center tw-gap-2 tw-text-sm tw-text-slate-600 tw-bg-slate-100 tw-z-10">
          <LoadingSpinner size="sm" /> Opening the marked-up file…
        </div>
      )}
      {status === 'error' && <div className="tw-p-4"><Notice tone="red">{error}</Notice></div>}
      {isPdf ? (
        pdfUrl && (
          <iframe key={page ? `${page}-${target.nonce}` : 'start'} title={`Marked-up ${comparison.sales_filename}`}
            src={page ? `${pdfUrl}#page=${page}` : pdfUrl} className="tw-w-full tw-h-full tw-border-0" />
        )
      ) : (
        <div ref={docxRef} className="tw-h-full tw-overflow-auto" aria-label={`Marked-up ${comparison.sales_filename}`} />
      )}
    </div>
  );
};

DocumentViewer.propTypes = {
  comparison: PropTypes.shape({
    id: PropTypes.number.isRequired, annotated_filename: PropTypes.string, sales_filename: PropTypes.string,
  }).isRequired,
  target: PropTypes.shape({ finding: PropTypes.object.isRequired, nonce: PropTypes.number.isRequired }),
};

export default DocumentViewer;
